import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';

import { Label } from '../ui/label';
import { Send, CheckCircle, XCircle, Loader2, AlertTriangle, FileText, Clock, User, MessageSquare, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WorkflowPublicationPanel = ({
  version,
  token,
  userRole,
  permissions,
  onVersionUpdate,
  lang = 'fr'
}) => {
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState('');
  const [showConfirm, setShowConfirm] = useState(null);

  const t = {
    fr: {
      workflow: "Workflow de publication",
      currentStatus: "Statut actuel",
      draft: "Brouillon",
      submitted: "En révision",
      approved: "Approuvé",
      published: "Publié",
      archived: "Archivé",
      submitReview: "Soumettre pour révision",
      approve: "Approuver",
      reject: "Rejeter",
      publish: "Publier",
      comments: "Commentaires",
      commentsPlaceholder: "Ajoutez des commentaires sur cette action...",
      confirmAction: "Confirmer l'action",
      cancel: "Annuler",
      submittedBy: "Soumis par",
      submittedAt: "Soumis le",
      approvedBy: "Approuvé par",
      approvedAt: "Approuvé le",
      publishedBy: "Publié par",
      publishedAt: "Publié le",
      requirements: "Prérequis",
      requireHardRule: "Au moins une règle HARD active requise",
      requireMessages: "Messages FR et EN requis pour toutes les règles",
      workflowSteps: "Étapes du workflow",
      step1: "1. Brouillon → Révision",
      step2: "2. Révision → Approbation",
      step3: "3. Approbation → Publication"
    },
    en: {
      workflow: "Publication Workflow",
      currentStatus: "Current Status",
      draft: "Draft",
      submitted: "Under Review",
      approved: "Approved",
      published: "Published",
      archived: "Archived",
      submitReview: "Submit for Review",
      approve: "Approve",
      reject: "Reject",
      publish: "Publish",
      comments: "Comments",
      commentsPlaceholder: "Add comments about this action...",
      confirmAction: "Confirm Action",
      cancel: "Cancel",
      submittedBy: "Submitted by",
      submittedAt: "Submitted on",
      approvedBy: "Approved by",
      approvedAt: "Approved on",
      publishedBy: "Published by",
      publishedAt: "Published on",
      requirements: "Requirements",
      requireHardRule: "At least one active HARD rule required",
      requireMessages: "FR and EN messages required for all rules",
      workflowSteps: "Workflow Steps",
      step1: "1. Draft → Review",
      step2: "2. Review → Approval",
      step3: "3. Approval → Publication"
    }
  };

  const statusConfig = {
    draft: { label: t[lang].draft, color: 'bg-gray-100 text-gray-800', icon: FileText },
    submitted: { label: t[lang].submitted, color: 'bg-yellow-100 text-yellow-800', icon: Clock },
    approved: { label: t[lang].approved, color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
    published: { label: t[lang].published, color: 'bg-green-100 text-green-800', icon: CheckCircle },
    archived: { label: t[lang].archived, color: 'bg-gray-100 text-gray-500', icon: FileText }
  };

  const currentStatusConfig = statusConfig[version?.status] || statusConfig.draft;
  const StatusIcon = currentStatusConfig.icon;

  const executeAction = async (action) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/versions/${version.id}/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ comments })
      });

      if (res.ok) {
        onVersionUpdate && onVersionUpdate();
        setComments('');
        setShowConfirm(null);
      } else {
        const error = await res.json();
        alert(error.detail || 'Erreur');
      }
    } catch {
      alert('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  // Determine available actions based on status and permissions
  const getAvailableActions = () => {
    const actions = [];
    const status = version?.status;
    const role = userRole;

    if (status === 'draft') {
      if (permissions?.can_edit_rules || role === 'admin' || role === 'rule_editor') {
        actions.push({
          key: 'submit-review',
          label: t[lang].submitReview,
          icon: Send,
          color: 'bg-blue-600 hover:bg-blue-700',
          description: 'Soumettre cette version pour révision par un approbateur'
        });
      }
    }

    if (status === 'submitted') {
      if (permissions?.can_approve || role === 'admin' || role === 'approver') {
        actions.push({
          key: 'approve',
          label: t[lang].approve,
          icon: CheckCircle,
          color: 'bg-green-600 hover:bg-green-700',
          description: 'Approuver cette version pour publication'
        });
        actions.push({
          key: 'reject',
          label: t[lang].reject,
          icon: XCircle,
          color: 'bg-red-600 hover:bg-red-700',
          description: 'Rejeter et renvoyer en brouillon'
        });
      }
    }

    if (status === 'approved') {
      if (permissions?.can_publish || role === 'admin' || role === 'publisher') {
        actions.push({
          key: 'publish',
          label: t[lang].publish,
          icon: CheckCircle,
          color: 'bg-purple-600 hover:bg-purple-700',
          description: 'Publier cette version (remplace la version actuelle)'
        });
      }
    }

    return actions;
  };

  const availableActions = getAvailableActions();

  return (
    <div className="space-y-6">
      {/* Current Status */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <StatusIcon className="w-5 h-5" />
            {t[lang].workflow}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-6">
            <span className="text-sm text-gray-500">{t[lang].currentStatus}:</span>
            <Badge className={`${currentStatusConfig.color} text-sm px-3 py-1`}>
              {currentStatusConfig.label}
            </Badge>
          </div>

          {/* Workflow visualization */}
          <div className="flex items-center justify-between mb-6 px-4">
            {['draft', 'submitted', 'approved', 'published'].map((step, index) => {
              const stepConfig = statusConfig[step];
              const StepIcon = stepConfig.icon;
              const isActive = version?.status === step;
              const isPast = ['draft', 'submitted', 'approved', 'published'].indexOf(version?.status) > index;

              return (
                <React.Fragment key={step}>
                  <div className={`flex flex-col items-center ${
                    isActive ? 'text-blue-600' : isPast ? 'text-green-600' : 'text-gray-300'
                  }`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isActive ? 'bg-blue-100 ring-2 ring-blue-500' : 
                      isPast ? 'bg-green-100' : 'bg-gray-100'
                    }`}>
                      <StepIcon className="w-5 h-5" />
                    </div>
                    <span className="text-xs mt-1 font-medium">{stepConfig.label}</span>
                  </div>
                  {index < 3 && (
                    <ChevronRight className={`w-6 h-6 ${
                      isPast ? 'text-green-400' : 'text-gray-200'
                    }`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* History */}
          <div className="space-y-2 border-t pt-4">
            {version?.submitted_by && (
              <div className="flex items-center gap-2 text-sm">
                <User className="w-4 h-4 text-gray-400" />
                <span className="text-gray-500">{t[lang].submittedBy}:</span>
                <span className="font-medium">{version.submitted_by}</span>
                {version.submitted_at && (
                  <span className="text-gray-400">
                    ({new Date(version.submitted_at).toLocaleDateString(lang)})
                  </span>
                )}
              </div>
            )}
            {version?.approved_by && (
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span className="text-gray-500">{t[lang].approvedBy}:</span>
                <span className="font-medium">{version.approved_by}</span>
                {version.approved_at && (
                  <span className="text-gray-400">
                    ({new Date(version.approved_at).toLocaleDateString(lang)})
                  </span>
                )}
              </div>
            )}
            {version?.published_by && (
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-purple-500" />
                <span className="text-gray-500">{t[lang].publishedBy}:</span>
                <span className="font-medium">{version.published_by}</span>
                {version.published_at && (
                  <span className="text-gray-400">
                    ({new Date(version.published_at).toLocaleDateString(lang)})
                  </span>
                )}
              </div>
            )}
            {version?.review_comments && (
              <div className="flex items-start gap-2 text-sm mt-2 p-2 bg-gray-50 rounded">
                <MessageSquare className="w-4 h-4 text-gray-400 mt-0.5" />
                <span className="text-gray-600">{version.review_comments}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Available Actions */}
      {availableActions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Actions disponibles</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Comments input */}
            <div>
              <Label>{t[lang].comments}</Label>
              <Textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder={t[lang].commentsPlaceholder}
                rows={2}
                className="mt-1"
              />
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-3">
              {availableActions.map(action => {
                const ActionIcon = action.icon;
                return (
                  <div key={action.key} className="flex-1 min-w-[200px]">
                    {showConfirm === action.key ? (
                      <div className="p-3 border rounded-lg bg-gray-50">
                        <p className="text-sm text-gray-600 mb-3">{action.description}</p>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => executeAction(action.key)}
                            disabled={loading}
                            className={action.color}
                          >
                            {loading ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                              <ActionIcon className="w-4 h-4 mr-2" />
                            )}
                            {t[lang].confirmAction}
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => setShowConfirm(null)}
                            disabled={loading}
                          >
                            {t[lang].cancel}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        onClick={() => setShowConfirm(action.key)}
                        className={`w-full ${action.color} text-white`}
                      >
                        <ActionIcon className="w-4 h-4 mr-2" />
                        {action.label}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Requirements Info */}
      {version?.status === 'draft' && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-yellow-800">
              <AlertTriangle className="w-4 h-4" />
              {t[lang].requirements}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li>• {t[lang].requireHardRule}</li>
              <li>• {t[lang].requireMessages}</li>
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default WorkflowPublicationPanel;
